<template>
  <v-menu v-model="isOpen" offset-y left :close-on-content-click="false">
    <template v-slot:activator="{ on }">
      <v-btn class="states-filter__trigger" small outlined v-on="on">
        <span class="states-filter__label">{{ triggerLabel }}</span>
        <v-icon small right>{{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
      </v-btn>
    </template>

    <div class="states-filter__panel">
      <v-text-field
        v-model="search"
        class="states-filter__search"
        prepend-inner-icon="mdi-magnify"
        :placeholder="$t('generic.search_label')"
        label=""
        hide-details
        dense
        outlined />

      <v-list class="states-filter__list">
        <v-list-item class="states-filter__all" :class="{ 'v-list-item--active': allSelected }" @click="selectAll">
          <v-list-item-action>
            <v-checkbox :input-value="allSelected" color="primary" readonly />
          </v-list-item-action>
          <v-list-item-content>
            {{ $t("resources.ab_tests.states_filter.all") }}
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-for="state in visibleStates"
          :key="state"
          :class="{ 'v-list-item--active': draft.includes(state) }"
          @click="toggleState(state)">
          <v-list-item-action>
            <v-checkbox :input-value="draft.includes(state)" color="primary" readonly />
          </v-list-item-action>
          <v-list-item-content>
            {{ state }}
          </v-list-item-content>
        </v-list-item>

        <v-list-item v-if="visibleStates.length === 0" disabled>
          <v-list-item-content>
            {{ $t("custom.no_data") }}
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <div class="states-filter__actions">
        <v-btn text @click="cancel">{{ $t("generic.buttons.cancel") }}</v-btn>
        <v-btn color="primary" @click="apply">{{ $t("generic.buttons.apply") }}</v-btn>
      </div>
    </div>
  </v-menu>
</template>

<script>
  import i18n from "@/i18n";

  const NAMED_SELECTION_LIMIT = 2;

  export default {
    name: "AbTestStatesFilter",
    props: {
      value: {
        type: Array,
        default: () => [],
      },
      states: {
        type: Array,
        required: true,
      },
    },
    data() {
      return {
        isOpen: false,
        search: "",
        draft: [],
      };
    },
    computed: {
      allSelected() {
        return this.draft.length === 0;
      },
      visibleStates() {
        const query = this.search.trim().toLowerCase();

        if (!query) {
          return this.states;
        }

        return this.states.filter(state => state.toLowerCase().includes(query));
      },
      triggerLabel() {
        return `${i18n.t("resources.ab_tests.states_filter.label")} ${this.selectionLabel}`;
      },
      selectionLabel() {
        if (this.value.length === 0) {
          return i18n.t("resources.ab_tests.states_filter.all_selected");
        }

        if (this.value.length <= NAMED_SELECTION_LIMIT) {
          return this.value.join(", ");
        }

        return i18n.t("resources.ab_tests.states_filter.count", { count: this.value.length });
      },
    },
    watch: {
      isOpen(open) {
        if (open) {
          this.resetDraft();
        }
      },
    },
    methods: {
      resetDraft() {
        this.search = "";
        this.draft = this.value.slice();
      },
      selectAll() {
        this.draft = [];
      },
      toggleState(state) {
        this.draft = this.draft.includes(state)
          ? this.draft.filter(selected => selected !== state)
          : [...this.draft, state];
      },
      cancel() {
        this.resetDraft();
        this.isOpen = false;
      },
      apply() {
        this.$emit("input", this.draft.slice());
        this.isOpen = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .states-filter {
    &__trigger {
      border-color: get-color("border");
      text-transform: none;
      letter-spacing: normal;
    }

    &__label {
      max-width: 220px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__panel {
      background: get-color("element-bg-100");
      border-radius: 4px;
      box-shadow: 0px 2px 10px rgba(69, 64, 78, 0.1);
      min-width: 260px;
      padding: 16px;
    }

    &__search {
      padding-bottom: 8px;
    }

    &__list {
      padding: 0;
      max-height: 300px;
      overflow-y: auto;

      ::v-deep {
        .v-list-item {
          padding: 0 8px;
          min-height: 40px;
          cursor: pointer;

          &.v-list-item--active {
            background-color: get-color("primary-20");
          }
        }

        .v-list-item__content {
          padding: 0;
        }

        .v-list-item__action {
          margin: 0 8px 0 0;
        }
      }
    }

    &__all {
      border-bottom: 1px solid get-color("border");
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      padding-top: 12px;
    }
  }
</style>
